import { useEffect, useState } from "react";

import {
  Button,
  Callout,
  InputGroup,
  Intent,
  Menu,
  MenuItem,
  Position,
} from "@blueprintjs/core";
import Select from "react-select";

import customLocationIcon from "@/../../hypetrain/static/img/custom-location.svg";
import googleMeetIcon from "@/../../hypetrain/static/img/google-meet.svg";
import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { SUBTEXT_COLOR } from "@/css/constants";
import { FullMeetingDocument, useSaveMeetingLocationMutation } from "@/graphql";
import { FullMeeting } from "@/types/meetings";
import { SelectFn, SelectOption } from "@/types/select";
import { FullMeetingUser } from "@/types/users";

const LOCATION_TYPE_LIST: SelectOption[] = [
  { value: "google", label: "Google Meet", icon: googleMeetIcon },
  { value: "other", label: "Other", icon: customLocationIcon },
];

interface UpdateLocationDialogProps {
  meeting: FullMeeting;
  user: FullMeetingUser;
  open: boolean;
  onClose: () => void;
}

function UpdateLocationDialog({
  meeting,
  user,
  open,
  onClose,
}: UpdateLocationDialogProps) {
  const [locationType, setLocationType] = useState(LOCATION_TYPE_LIST[0]);
  const [errorMessage, setErrorMessage] = useState("");
  const [customLocation, setCustomLocation] = useState("");
  const meetingId = meeting?.id;
  const userId = user?.id;

  const refetchQueries = [
    {
      query: FullMeetingDocument,
      variables: { id: meetingId, customerId: userId },
    },
  ];

  const onChange: SelectFn<SelectOption> = (option) => {
    if (option) {
      setLocationType(option);
      setErrorMessage("");
    }
  };

  const onChangeCustomLocation = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomLocation(e.target.value);

    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const onSave = () => {
    if (locationType === LOCATION_TYPE_LIST[1] && !customLocation) {
      setErrorMessage("Custom Location cannot be empty");
      return;
    }
    saveMeetingLocation({
      variables: {
        meetingId,
        customLocation:
          locationType == LOCATION_TYPE_LIST[0]
            ? undefined
            : customLocation?.trim(),
      },
    });
  };

  const [saveMeetingLocation, { loading }] = useSaveMeetingLocationMutation({
    onCompleted: (data) => {
      const ok = data?.saveMeetingLocation?.ok;
      if (ok) {
        onClose();
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    },
    onError: (err) => {
      setErrorMessage(err.message);
    },
    refetchQueries: refetchQueries,
  });

  useEffect(() => {
    if (meeting?.customLocation) {
      setLocationType(LOCATION_TYPE_LIST[1]);
      setCustomLocation(meeting.customLocation);
    }
  }, [meeting]);

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title={<Box fontSize={18}>Change Session Location</Box>}
      width="480px"
      onClose={() => {
        onClose();
      }}
    >
      <Box py={3} px={3} css={{ borderRadius: "0 0 12px 12px" }}>
        <Select
          value={LOCATION_TYPE_LIST.find(
            (option) => option?.value == locationType?.value,
          )}
          onChange={onChange}
          isSearchable={true}
          options={LOCATION_TYPE_LIST}
          formatOptionLabel={(option) => (
            <Flex alignItems="center">
              <EmbedSvg src={option.icon} height={16} width={16} />
              <Box mx={2} fontSize={16} overflow="hidden">
                {option.label}
              </Box>
            </Flex>
          )}
        />
        <Box mt={3}>
          {locationType === LOCATION_TYPE_LIST[0] ? (
            <Box color={SUBTEXT_COLOR} fontSize={16}>
              Google Meeting URL will be generated by the system.
            </Box>
          ) : (
            <Box>
              <InputGroup
                onChange={onChangeCustomLocation}
                value={customLocation}
                large={true}
                fill={true}
                placeholder="Custom Location"
                autoFocus={true}
                disabled={loading}
              />
            </Box>
          )}
        </Box>

        {errorMessage && (
          <Box mt={3}>
            <Callout icon={null} intent={Intent.DANGER}>
              {errorMessage}
            </Callout>
          </Box>
        )}

        <Box mt={3}>
          <PrimaryButton
            intent="primary"
            disabled={loading}
            loading={loading}
            onClick={onSave}
          >
            Save
          </PrimaryButton>
        </Box>
      </Box>
    </StyledDialog>
  );
}

interface MeetingLocationMenuProps {
  meeting: FullMeeting;
  user: FullMeetingUser;
}

export function MeetingLocationMenu({
  meeting,
  user,
}: MeetingLocationMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const menuContent = (
    <Menu>
      <MenuItem
        onClick={() => {
          setIsDialogOpen(true);
          setIsOpen(false);
        }}
        text="Change Location"
      />
    </Menu>
  );

  return (
    <>
      <Popover
        content={menuContent}
        canEscapeKeyClose={true}
        position={Position.BOTTOM_RIGHT}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <Button
          style={{ minHeight: 36, marginLeft: 5 }}
          minimal={true}
          icon="more"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        />
      </Popover>
      <UpdateLocationDialog
        meeting={meeting}
        user={user}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
}
